import { useContext, useState } from 'react';
import { Tab, TabList } from '@fluentui/react-components';
import { ClipboardTaskListLtrRegular } from '@fluentui/react-icons';
import { ITEMS_NAV_DRAWER_WIDTH_PX } from '@modules/items/constants';
import { NAV_DRAWER_COLLAPSED_WIDTH_PX } from '@shared/components/nav/NavDrawer';
import { Path } from '@shared/components/Path';
import { useFolderPathAsBreadcrumbs } from '@shared/hooks/useFolderPathAsBreadcrumbs.hook';
import { useSearchParams } from '@shared/hooks/useSearchParams.hook';
import { layoutTransition } from '@shared/constants/layoutTransition.constants';
import { TERMINOLOGY } from '@shared/i18n';
import type { IFolder, TPath } from '@modules/items/types/folders.types';
import { AccessType } from '@modules/access/types';
import { useCanEdit } from '@modules/access/hooks/useCanEdit.hook';
import { SignInContext } from '@modules/users';
import ItemsNavDrawer from '@modules/items/components/nav/ItemsNavDrawer';
import { isTemplateProject } from '@modules/mocks/projectTemplates';
import { useUpdateProject, useSetProjectStatus } from '../api';
import { getProjectPath, getProjectsPath } from '../paths';
import { DEFAULT_PROJECT_VIEW, PROJECT_DETAILS_MAX_WIDTH_PX, PROJECT_VIEW_PARAM } from '../constants';
import { EProjectView, type TProjectDetails } from '../types';
import ProjectDialog from './ProjectDialog';
import ProjectDetailsOverview from './ProjectDetailsOverview';
import ProjectTaskViews from './ProjectTaskViews';

type TProps = TProjectDetails & {
  folder: IFolder;
};

const ProjectDetailsContent = ({ folder, project, permissions }: TProps) => {
  const { user } = useContext(SignInContext);
  const { canEdit } = useCanEdit(permissions.accessRight, { authorGroup: false });
  const update = useUpdateProject(project._id);
  const setStatus = useSetProjectStatus(project._id);
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);
  const { organization } = useContext(SignInContext);
  // Mock: a template project opens as if just created from the template.
  const [isEditOpen, setIsEditOpen] = useState(
    () => canEdit && isTemplateProject(organization.mock?.projectTemplates, project._id)
  );
  const { searchParams, change } = useSearchParams();
  const requestedView = searchParams.get(PROJECT_VIEW_PARAM);
  const view = Object.values(EProjectView).find((value) => value === requestedView) ?? DEFAULT_PROJECT_VIEW;
  const drawerWidth = isDrawerOpen ? ITEMS_NAV_DRAWER_WIDTH_PX : NAV_DRAWER_COLLAPSED_WIDTH_PX;
  const fullPath: TPath = [
    ...folder.resolvedPath,
    { ...folder, href: getProjectsPath(folder._id) },
    {
      _id: project._id,
      name: project.name,
      icon: ClipboardTaskListLtrRegular,
      accessRight: AccessType.NONE,
      href: getProjectPath(project._id),
    },
  ];
  return (
    <div
      className="flex flex-1 min-h-0"
      style={{
        paddingRight: `clamp(0px, calc(100vw - ${PROJECT_DETAILS_MAX_WIDTH_PX}px), ${drawerWidth / 2}px)`,
        transition: layoutTransition('padding-right'),
      }}
    >
      <ItemsNavDrawer
        isOpen={isDrawerOpen}
        setIsOpen={setIsDrawerOpen}
        hrefBack={getProjectsPath(folder._id)}
        resolvedPath={fullPath}
        tabs={{
          Overview: (
            <ProjectDetailsOverview
              project={project}
              canEdit={canEdit}
              isPending={setStatus.isPending || update.isPending}
              onEdit={() => setIsEditOpen(true)}
              onStatusChange={(status) => setStatus.mutate(status)}
            />
          ),
        }}
      />
      <main className="flex min-w-0 min-h-0 flex-1 flex-col gap-5 pt-4">
        <div
          className="mx-auto flex w-full shrink-0 flex-col gap-5 pl-5 pr-8"
          style={{ maxWidth: PROJECT_DETAILS_MAX_WIDTH_PX }}
        >
          <Path items={useFolderPathAsBreadcrumbs(fullPath)} hasCurrent size="large" />
          <TabList
            size="medium"
            selectedValue={view}
            onTabSelect={(_, data) => {
              if (data.value !== view) change(PROJECT_VIEW_PARAM, data.value as EProjectView, { replace: false });
            }}
          >
            <Tab value={EProjectView.TASKS}>{TERMINOLOGY.task.upperPlural}</Tab>
            <Tab value={EProjectView.DASHBOARD}>Dashboard</Tab>
          </TabList>
        </div>
        <ProjectTaskViews project={project} view={view} rootItemId={folder.resolvedPath[0]._id} />
      </main>
      <ProjectDialog
        isOpen={isEditOpen}
        setIsOpen={setIsEditOpen}
        rootItemName={folder.resolvedPath[0].name}
        project={project}
        currentUserId={user._id}
        canEditTeam={permissions.accessRight >= AccessType.MANAGE}
        onSave={async (_, changes) => {
          const saved = await update.mutateAsync(changes);
          // Leaving the team can end this user's access; the cached tasks would keep their old actions.
          if (saved.permissions.accessRight < AccessType.READ) window.location.reload();
        }}
      />
    </div>
  );
};

export default ProjectDetailsContent;
